import React, { useState, useCallback } from 'react';

// ═══════════════ SOS BUTTON ═══════════════
export function SOSButton({ onActivate, style = {} }) {
  const [confirming, setConfirming] = useState(false);
  const [activated, setActivated] = useState(false);

  const handlePress = () => setConfirming(true);
  const handleConfirm = () => {
    setActivated(true);
    onActivate?.();
  };
  const handleCancel = () => setConfirming(false);

  if (activated) {
    return (
      <div className="fixed bottom-6 right-6" style={{ zIndex: 9999, ...style }}>
        <div className="px-6 py-4 rounded-2xl text-white font-bold text-center" style={{ background: '#ff6b6b', boxShadow: '0 4px 20px rgba(255,107,107,0.5)' }}>
          SOS Active — Help is on the way
          <div className="text-[0.8rem] font-normal mt-1">Admin has been alerted</div>
        </div>
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999, background: 'rgba(0,0,0,0.7)' }}>
        <div className="bg-white rounded-3xl p-8 max-w-[340px] text-center">
          <div className="text-[2rem] mb-3">🚨</div>
          <h3 className="mt-0 mb-2 text-brand-dark">Activate Emergency SOS?</h3>
          <p className="text-body text-[0.9rem] mt-0 mb-6">
            This will alert admin and send your GPS location to your emergency contacts.
          </p>
          <div className="flex gap-3">
            <button onClick={handleCancel} className="flex-1 p-3.5 rounded-full border-[1.5px] border-slate-200 bg-white cursor-pointer font-semibold">Cancel</button>
            <button onClick={handleConfirm} className="flex-1 p-3.5 rounded-full border-0 text-white cursor-pointer font-bold" style={{ background: '#ff6b6b' }}>Confirm SOS</button>
          </div>
          <button onClick={() => window.location.href = 'tel:000'} className="w-full mt-3 p-3.5 rounded-full border-0 text-white cursor-pointer font-bold text-[1rem]" style={{ background: '#0a1628' }}>Call 000 (Emergency)</button>
        </div>
      </div>
    );
  }

  return (
    <button onClick={handlePress} className="fixed bottom-6 right-6 w-16 h-16 rounded-full border-0 text-white text-[0.85rem] font-extrabold cursor-pointer"
      style={{ zIndex: 9999, background: '#ff6b6b', boxShadow: '0 4px 20px rgba(255,107,107,0.4)', ...style }}>SOS</button>
  );
}

// ═══════════════ STAR RATING ═══════════════
export function StarRating({ value = 0, onChange, size = 36, readonly = false }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} role="button" tabIndex={readonly ? -1 : 0}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHovered(star)}
          onMouseLeave={() => !readonly && setHovered(0)}
          style={{
            fontSize: size, cursor: readonly ? 'default' : 'pointer', transition: 'transform 0.15s',
            transform: hovered === star ? 'scale(1.2)' : 'scale(1)',
            color: star <= (hovered || value) ? '#fbbf24' : '#e2e8f0',
          }}>★</span>
      ))}
    </div>
  );
}

// ═══════════════ EMERGENCY CONTACTS MANAGER ═══════════════
export function EmergencyContactsManager({ contacts = [], onAdd, onRemove, maxContacts = 3 }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', relationship: '' });

  const handleAdd = () => {
    if (!form.name || !form.phone) return;
    onAdd?.(form);
    setForm({ name: '', phone: '', relationship: '' });
    setAdding(false);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h3 className="m-0 text-[1.1rem] text-brand-dark">Emergency Contacts</h3>
        {contacts.length < maxContacts && !adding && (
          <button onClick={() => setAdding(true)} className="px-4 py-2 rounded-full border-0 bg-brand-primary text-white font-semibold cursor-pointer text-[0.85rem]">+ Add</button>
        )}
      </div>

      {contacts.map((c) => (
        <div key={c.id} className="flex justify-between items-center p-3.5 rounded-xl border border-slate-200 mb-2">
          <div>
            <div className="font-semibold text-brand-dark">{c.name}</div>
            <div className="text-[0.85rem] text-body">{c.phone} · {c.relationship}</div>
          </div>
          <button onClick={() => onRemove?.(c.id)} className="px-3 py-1.5 rounded-full bg-transparent cursor-pointer text-[0.8rem]" style={{ border: '1px solid #ff6b6b', color: '#ff6b6b' }}>Remove</button>
        </div>
      ))}

      {contacts.length === 0 && !adding && (
        <div className="p-8 text-center text-muted text-[0.85rem]">
          No emergency contacts added. Add up to {maxContacts}.
        </div>
      )}

      {adding && (
        <div className="p-4 rounded-xl" style={{ border: '1px solid #2dd4a8', background: 'rgba(45,212,168,0.04)' }}>
          <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full p-2.5 rounded-lg border-[1.5px] border-slate-200 mb-2 outline-none" />
          <input placeholder="Phone (+614...)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full p-2.5 rounded-lg border-[1.5px] border-slate-200 mb-2 outline-none" />
          <input placeholder="Relationship" value={form.relationship} onChange={(e) => setForm({ ...form, relationship: e.target.value })}
            className="w-full p-2.5 rounded-lg border-[1.5px] border-slate-200 mb-3 outline-none" />
          <div className="flex gap-2">
            <button onClick={() => setAdding(false)} className="flex-1 p-2.5 rounded-full border border-slate-200 bg-white cursor-pointer">Cancel</button>
            <button onClick={handleAdd} className="flex-1 p-2.5 rounded-full border-0 bg-brand-primary text-white cursor-pointer font-semibold">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}
